import PropTypes from "prop-types";
import { useUndoKey } from "src/utils/lastUndo";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, Typography } from "@mui/material";
import ActionButton from "src/components/shared/ActionButton";
import { casesApi } from "./api";
import { STATUS } from "./format";
import { toneColor } from "./workspace";

const AT_ONCE = 4;
const LISTED = 8;

const applications = (n) => (n === 1 ? "1 application" : `${n} applications`);

// Runs one call per case, four at a time; answers how many failed.
async function eachCase(items, call, onProgress) {
  let done = 0;
  let failed = 0;
  const queue = [...items];
  const worker = async () => {
    while (queue.length) {
      const item = queue.shift();
      try {
        // eslint-disable-next-line no-await-in-loop
        await call(item);
      } catch {
        failed += 1;
      }
      done += 1;
      onProgress?.({ done, failed });
    }
  };
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, items.length) }, worker));
  return failed;
}

// The message after a bulk cancel, with Undo: each case goes back to the
// status it had, still without emailing anyone.
function UndoToast({ count, onUndo }) {
  const [state, setState] = useState("ready");
  const press = async () => {
    if (state !== "ready") return;
    setState("undoing");
    const failed = await onUndo();
    setState(failed ? "partly" : "undone");
  };
  // Ctrl+Z presses it too, for a minute (georges-09).
  useUndoKey(press);
  return (
    <Box>
      <Typography variant="body2">Cancelled {applications(count)}. No one was emailed.</Typography>
      {state === "ready" && (
        <ActionButton variant="text" aria-keyshortcuts="Control+Z" onClick={press} sx={{ mt: 0.5, ml: -1.5 }}>
          Undo
        </ActionButton>
      )}
      {state === "undoing" && <Typography variant="body2">Putting them back…</Typography>}
      {state === "undone" && <Typography variant="body2">Undone: every case is back as it was.</Typography>}
      {state === "partly" && <Typography variant="body2">Some couldn’t be put back. Open them to check.</Typography>}
    </Box>
  );
}

UndoToast.propTypes = { count: PropTypes.number.isRequired, onUndo: PropTypes.func.isRequired };

// Cancels many cases at once, for clearing out stale or test applications
// (CMS review rania-09, nadia-19). Never emails: every call sends
// notify:false. It names the cases first, asks for their number when there
// are several, and offers Undo afterwards.
export default function BulkCancel({ cases, onClose, onDone, onUndone }) {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(null); // { done, failed }
  const [typed, setTyped] = useState("");
  const running = progress && progress.done < cases.length;
  const n = cases.length;
  const mustType = n > 1;
  const confirmed = !mustType || typed.trim() === String(n);

  const run = async () => {
    const before = cases.map((item) => ({ id: item.id, status: item.status }));
    setProgress({ done: 0, failed: 0 });
    const failed = await eachCase(cases, (item) => casesApi.status(item.id, "cancelled", "", dispatch, false), setProgress);
    const cancelled = n - failed;
    if (failed) {
      toast.warn(`Cancelled ${cancelled} of ${n}. ${failed} could not be cancelled; try them again. No one was emailed.`);
    } else {
      const undo = async () => {
        const left = await eachCase(before, (item) => casesApi.status(item.id, item.status, "Undo of a bulk cancel", dispatch, false));
        onUndone?.();
        return left;
      };
      toast.success(<UndoToast count={cancelled} onUndo={undo} />, { autoClose: 20000, closeOnClick: false });
    }
    onDone();
  };

  return (
    <Dialog open onClose={running ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="bulk-cancel-title" aria-describedby="bulk-cancel-what">
      <DialogTitle id="bulk-cancel-title">Cancel {applications(n)}?</DialogTitle>
      <DialogContent>
        <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }} aria-label="These applications">
          {cases.slice(0, LISTED).map((item) => (
            <Typography component="li" variant="body2" key={item.id}>
              <b>#{item.id}</b> {item.customer?.name || "—"} · {item.destination || "—"} · {STATUS[item.status]?.label || item.status}
            </Typography>
          ))}
          {n > LISTED && (
            <Typography component="li" variant="body2" color="text.secondary">
              and {n - LISTED} more
            </Typography>
          )}
        </Box>
        <Typography variant="body2" id="bulk-cancel-what">
          Every traveller on {n === 1 ? "it" : "them"} moves to Cancelled and each case’s timeline records it. The customers are <b>not</b> emailed. You can undo it
          from the message that follows.
        </Typography>
        {mustType && !progress && (
          <TextField
            label={`Type ${n} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputProps={{ inputMode: "numeric", "aria-describedby": "bulk-cancel-what" }}
            fullWidth
            sx={{ mt: 2 }}
            autoFocus
          />
        )}
        {progress && (
          <Box mt={2} role="status" aria-live="polite">
            <LinearProgress variant="determinate" value={(progress.done / n) * 100} sx={{ height: 6, borderRadius: 3 }} />
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              {running ? `Cancelling ${progress.done} of ${n}…` : `Done: ${n - progress.failed} cancelled`}
              {progress.failed ? `, ${progress.failed} failed` : ""}.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose} disabled={!!running}>
          Keep them
        </ActionButton>
        <ActionButton
          size="medium"
          variant="contained"
          busyLabel="Cancelling…"
          disabled={!!progress || !confirmed}
          onClick={run}
          sx={(t) => ({
            bgcolor: toneColor("fail")(t),
            color: t.palette.mode === "dark" ? "#1a1a1a" : "#fff",
            boxShadow: "none",
            "&:hover": { bgcolor: toneColor("fail")(t), boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: toneColor("fail")(t), color: t.palette.mode === "dark" ? "#1a1a1a" : "#fff" },
          })}
        >
          Cancel {applications(n)}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

BulkCancel.propTypes = {
  cases: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onUndone: PropTypes.func,
};
